"use client";
import { ReactNode } from "react";
import { motion } from "framer-motion";
import { usePathname } from "next/navigation";
import useDeviceType from "@/app/_hooks/useDeviceType";
import { HEADER_HEIGHT, NAV_WIDTH } from "@/app/_constants/ui";
import {
  Anchor,
  AppShell as AppShellMantine,
  Blockquote,
  Box,
  Collapse,
  Group,
  Modal,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import Spotlight from "../Spotlight/Spotlight";
import MobileHeader from "./components/MobileHeader/MobileHeader";
import DesktopHeader from "./components/DesktopHeader/DesktopHeader";

import WalkThrough from "../WalkThrough/WalkThrough";
import WhatsNewModal from "../WhatsNewModal/WhatsNewModal";
import { useUserContext } from "@/app/_hooks/useUserContext";
import { useSubscriptionContext } from "@/app/_hooks/subscription/useSubscriptionContext";
import SettingsNav from "./components/SettingsNav/SettingsNav";
import { NamedUserUID } from "@/app/@types/enums";
import { useDisclosure } from "@mantine/hooks";
import { SupportProvider } from "@/app/_providers/SupportProvider/SupportProvider";
import SupportDrawer from "../Support/SupportDrawer";

interface AppShellProps {
  children: ReactNode;
}

const AppShell = ({ children }: AppShellProps) => {
  const { isMobile } = useDeviceType();
  const user = useUserContext();
  const subscription = useSubscriptionContext();
  const pathname = usePathname();
  const isSettingsPage = pathname.includes("/settings");
  const [opened, { toggle: toggleOpened }] = useDisclosure(false);

  return (
    <SupportProvider>
      <Modal
        opened={!subscription.active}
        closeOnEscape={false}
        withCloseButton={false}
        onClose={() => {}}
        overlayProps={{ opacity: 1, style: { backdropFilter: "blur(5px)" } }}
        title={
          <Title>
            {user.uid === NamedUserUID.TMT
              ? "Subscription Overdue"
              : "No Active Subscription"}
          </Title>
        }
        centered
        size="xl"
        trapFocus={false}
      >
        <Stack gap="lg">
          <Text size="lg">
            An active subscription is required to use MountMonitor
          </Text>
          <Text size="lg">
            If you are not the store owner, please contact the owner.
          </Text>
          <Text size="lg">
            If you are the store owner, please{" "}
            <Anchor href="mailto:contact@mountmonitor.com" target="_blank">
              contact support
            </Anchor>{" "}
            to get started with your subscription.
          </Text>
        </Stack>
      </Modal>
      {user.walkThroughCompleted === false ? <WalkThrough /> : null}
      <WhatsNewModal />
      <AppShellMantine
        header={{ height: HEADER_HEIGHT, collapsed: !isMobile }}
        navbar={{
          width: NAV_WIDTH,
          breakpoint: "sm",
          collapsed: { mobile: true },
        }}
      >
        <AppShellMantine.Header withBorder={false} style={{ border: "none" }}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
          >
            <MobileHeader />
          </motion.div>
        </AppShellMantine.Header>
        <AppShellMantine.Navbar>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
            style={{ height: "100%", width: "100%" }}
          >
            <DesktopHeader />
          </motion.div>
        </AppShellMantine.Navbar>
        <AppShellMantine.Main>
          <Group wrap="nowrap" gap="0">
            <Box visibleFrom="sm">
              {isSettingsPage && (
                <motion.div
                  initial={{ x: -300, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: -300, opacity: 0 }}
                  style={{ height: "100%" }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                >
                  <SettingsNav />
                </motion.div>
              )}
            </Box>

            {children}
          </Group>
          <Spotlight />
        </AppShellMantine.Main>
      </AppShellMantine>
      <SupportDrawer />
    </SupportProvider>
  );
};

export default AppShell;
